import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import WineSearchBox from './WineSearchBox';
import inventoryItemService from '../../services/inventoryItemService';
import type { WineSearchResult } from '../../services/wineService';

/**
 * SIMPLE-WINE.4 §7 — linking an item to a catalogue wine, in a dialog.
 *
 * This is the same flow that shipped as a panel section, re-housed. The panel
 * sat several sections below the item header, so the state ("Not linked to
 * wine catalog") and the remedy never appeared together, and the first
 * production walk-through concluded the feature had not shipped. Scrolling to
 * the panel was tried and then rejected: the answer to "the fix is far away"
 * is to bring it to the click, not to travel to it.
 *
 * Deliberately NOT a new mechanism. The picker is the same WineSearchBox with
 * the same did-you-mean behaviour, and the write is the same existing item
 * update path — which already validates the wine, rebuilds displayName from
 * the canonical name, and refuses a venue name collision. A second link
 * mechanism would mean a second copy of those rules, and they would drift.
 */

export interface WineCatalogLinkDialogProps {
  open: boolean;
  venueId: string;
  itemId: string;
  /** Size, purely so the rename warning can name the suffix it will add. */
  sizeValue?: number | null;
  sizeUnit?: string | null;
  /** Called with the re-read item after a successful link. */
  onLinked: (item: unknown) => void;
  onClose: () => void;
}

const WineCatalogLinkDialog: React.FC<WineCatalogLinkDialogProps> = ({
  open, venueId, itemId, sizeValue, sizeUnit, onLinked, onClose,
}) => {
  const [candidate, setCandidate] = useState<WineSearchResult | null>(null);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState('');

  // Re-arm on each open: the dialog stays mounted, and a candidate left over
  // from a cancelled attempt must not be sitting there pre-selected.
  useEffect(() => {
    if (!open) return;
    setCandidate(null);
    setError('');
    setLinking(false);
  }, [open]);

  const handleLink = useCallback(async () => {
    if (!candidate) return;
    setLinking(true);
    setError('');
    try {
      await inventoryItemService.updateItem(venueId, itemId, { wineId: candidate._id });
      // Re-read rather than patching local state: the server rebuilt the
      // displayName, and guessing at it here would show a name the record
      // does not have.
      const fresh = await inventoryItemService.getItem(venueId, itemId);
      onLinked(fresh);
      onClose();
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      setError(code === 'INVENTORY_ITEM_DUPLICATE'
        ? 'Linking would give this item the same name as another active item at '
          + 'this venue. Resolve that duplicate first.'
        : (err?.response?.data?.error?.message
          || 'Could not link this item. Nothing was changed.'));
    } finally {
      setLinking(false);
    }
  }, [candidate, venueId, itemId, onLinked, onClose]);

  const sizeSuffix = sizeValue && sizeUnit ? ` (plus its ${sizeValue} ${sizeUnit} size)` : '';

  return (
    <Dialog
      open={open}
      onClose={linking ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="wine-catalog-link-title"
    >
      <DialogTitle id="wine-catalog-link-title" sx={{ fontWeight: 600 }}>
        Link to wine catalog
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This item is not linked to a wine in the catalog. It usually means the
          wine it was created against was rejected during review.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <WineSearchBox
          value={candidate}
          onChange={setCandidate}
          venueId={venueId}
          label="Search the wine catalog"
          disabled={linking}
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          Linking renames this item to the catalog name{sizeSuffix}. The name
          you see now will be replaced.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={linking} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!candidate || linking}
          onClick={handleLink}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {linking ? 'Linking…' : 'Link this wine'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WineCatalogLinkDialog;
